<template>
  <BaseModal
    :open="!!badge"
    :title="title"
    :z-index="10001"
    size="sm"
    data-test="badge-info-modal"
    @close="emit('close')"
  >
    <div v-if="badge" class="bdi">
      <img
        class="bdi__icon"
        :src="image"
        :alt="title"
        width="160"
        height="160"
      />

      <h2 class="bdi__title" data-test="badge-info-title">{{ title }}</h2>

      <p class="bdi__status"
:class="badge.earned ? 'is-earned' : 'is-locked'"
data-test="badge-info-status">
        {{ statusLabel }}
      </p>

      <!-- В момент выдачи вместо условия идёт поздравление: человек знак уже
           получил, и «как получить» ему читать незачем. -->
      <p v-if="praise" class="bdi__text" data-test="badge-info-praise">{{ praise }}</p>

      <template v-else>
        <h3 class="bdi__how">{{ t('vpn.badges.modal.howTo') }}</h3>
        <p class="bdi__text" data-test="badge-info-howto">{{ howTo }}</p>

        <!-- Ступени лестницы накопительны: без этой оговорки условие «набери
             60 000 опыта» читается как единственное, а оно не единственное. -->
        <p v-if="badge.collection === 'league'" class="bdi__note">
          {{ t('vpn.badges.modal.ladderNote') }}
        </p>
      </template>

      <button type="button"
class="bdi__close"
data-test="badge-info-close"
@click="emit('close')">
        {{ t('vpn.badges.modal.close') }}
      </button>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
/**
 * IGRA-112 — карточка одной награды: что это за знак и что сделать, чтобы его
 * получить.
 *
 * Тексты условий целиком в локалях по коду бейджа
 * (`vpn.badges.item.<code>.title/.howTo`). Бэкенд про них не знает намеренно:
 * реестр `config/badges.php` хранит код, коллекцию и порядок, а человеческое
 * объяснение — это UI-строка, и место ей там же, где остальным UI-строкам.
 *
 * Наград, у которых триггера выдачи в коде пока нет, одиннадцать. Им написан
 * честный общий текст «правило ещё не запущено» — выдумывать условие, которого
 * никто не реализовал, значит обещать невыполнимое.
 *
 * IGRA-409 — необязательный проп `praise`. Его передаёт только окно выдачи
 * (`ForcedNoticeModal`), и тогда вместо «Как получить» показывается
 * поздравление: за что знак пришёл и какая цель следующая. Текст приходит с
 * бэкенда строкой уведомления, потому что тем же текстом награда уходит в
 * телеграм, а фронтовых локалей телеграм не видит. Витрина проп не передаёт и
 * ведёт себя как раньше.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import BaseModal from '@/shared/components/base/BaseModal.vue'
import type { VpnBadge } from '../types/badges'
import { formatDateLong } from '../utils/vpnFormat'

const props = defineProps<{ badge: VpnBadge | null; image: string; praise?: string }>()
const emit = defineEmits<{ close: [] }>()

const { t, locale } = useI18n()

/** Название из локалей; бэкендовое остаётся запасным — новый код в реестре не
 *  должен показывать человеку голый ключ перевода. */
const title = computed(() => {
  const badge = props.badge
  if (!badge) return ''
  const key = `vpn.badges.item.${badge.code}.title`
  const translated = t(key)

  return translated === key ? badge.title : translated
})

const howTo = computed(() => {
  const badge = props.badge
  if (!badge) return ''
  const key = `vpn.badges.item.${badge.code}.howTo`
  const translated = t(key)

  return translated === key ? t('vpn.badges.modal.unknown') : translated
})

const statusLabel = computed(() => {
  const badge = props.badge
  if (!badge) return ''
  if (!badge.earned) return t('vpn.badges.modal.locked')

  const date = formatDateLong(badge.earnedAt, locale.value)

  return date ? t('vpn.badges.modal.earnedAt', { date }) : t('vpn.badges.modal.earned')
})
</script>

<style scoped lang="scss">
// Раскладка и токены — как у StarsHowModal.vue: раздел наград обязан читаться
// как продолжение кабинета, а не как отдельный экран.
.bdi {
  text-align: center;

  &__icon {
    width: 100%;
    max-width: 160px;
    height: auto;
  }

  &__title {
    margin: 12px 0 0;
    font-family: 'Geologica', var(--p-font-display);
    font-weight: 700;
    font-size: 22px;
    line-height: 1.15;
    color: var(--ds-text);
  }

  &__status {
    margin: 8px 0 0;
    font-family: var(--p-font-code);
    font-size: 13px;

    &.is-earned {
      color: var(--ds-accent);
    }

    &.is-locked {
      color: var(--ds-text-secondary);
    }
  }

  &__how {
    margin: 20px 0 0;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--ds-text-secondary);
  }

  &__text {
    margin: 8px 0 0;
    font-size: 16px;
    line-height: 1.5;
    text-align: left;
    color: var(--ds-text);
  }

  &__note {
    margin: 12px 0 0;
    font-size: 14px;
    line-height: 1.5;
    text-align: left;
    color: var(--ds-text-secondary);
  }

  &__close {
    margin-top: 20px;
    width: 100%;
    padding: 12px 18px;
    border: 1px solid var(--ds-border);
    border-radius: var(--ds-radius-control);
    background: transparent;
    color: var(--ds-text);
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
      border-color: var(--ds-accent);
    }
  }
}
</style>
